/**
 * Where the store button points.
 *
 * Mirrors `apps/mobile/src/constants/links.ts`. It used to be a `const` copied
 * into `routes/index.tsx` and `components/SharedItem.tsx`, which is one copy
 * per surface that sells the app and therefore one place per surface for the id
 * to go stale — and a wrong App Store id does not 404, it opens somebody else's
 * app.
 *
 * ── Why there is no Play URL ─────────────────────────────────────────────────
 *
 * There was one, pointing at `co.akes.bibleshield`, and Google Play answers it
 * with a 404: the Android build is still in development and has never been
 * published. A store button that 404s is worse than no button — it reads as an
 * app that was pulled. So every Android surface now says "coming soon" instead,
 * and the constant is gone rather than kept "for later", because a URL sitting
 * in the file is a URL somebody wires up again.
 *
 * When Android does ship, the pieces to restore are: this constant, the second
 * button in `Landing`/`StoreCta`/`SharedItem`, the `installUrl` and `sameAs`
 * arrays and `operatingSystem` in `lib/seo.ts`, the "Get the app" list in
 * `lib/llms.ts`, and the Android answer in all nine `lib/marketing/*.ts` FAQs.
 */

/** The numeric App Store id, on its own because the JSON-LD needs it too. */
export const APPLE_ITUNES_ID = "6787711829";

/**
 * The canonical App Store listing.
 *
 * This is the FACTUAL url — the one that goes in `installUrl`, in `sameAs` and
 * in `llms.txt`. Structured data is a set of claims about what this entity is,
 * and pointing an entity claim at a measurement redirect both weakens the link
 * between the domain and the listing and states something that is not quite
 * true. Buttons are a different job; see `appStoreLink`.
 */
export const APP_STORE_URL = `https://apps.apple.com/app/id${APPLE_ITUNES_ID}`;

/**
 * The AppsFlyer OneLink template every web install goes through.
 *
 * ── Why the buttons do not point straight at Apple ───────────────────────────
 *
 * Without this, every install from this site arrives in AppsFlyer as *organic*
 * and is indistinguishable from someone who found the app in App Store search.
 * The blog, the quizzes and the verse pages exist to produce installs, and until
 * the click passes through a measurable link there is no number that says
 * whether any of it worked. `web_store_click` in PostHog already records the tap
 * — what it cannot see is the other side of the handoff.
 *
 * ── Why this exact subdomain, id and path ────────────────────────────────────
 *
 * `bibleshield.onelink.me` is compiled into the iOS binary as an associated
 * domain and into the Android manifest as an App Links filter, and the template
 * id `ocTL` is what AppsFlyer's `apple-app-site-association` serves paths for.
 * Neither is editable from here: a different subdomain is a domain this build
 * cannot open, and fixing that needs a new App Store build (docs/ATTRIBUTION.md
 * §2.4).
 *
 * `/web` is a REGISTERED custom link (`website_app_store_cta` in the dashboard,
 * created 27 Aug 2026) rather than a bare template url with parameters hung off
 * it, and the difference is not cosmetic. The AASA covers `/ocTL/*` — a path —
 * so this url is a Universal Link: a visitor who already has the app installed
 * opens the app instead of being bounced to a store listing for something they
 * own. `https://bibleshield.onelink.me/ocTL?pid=…` is the same redirect without
 * that, because a query string is not a path.
 *
 * ── What the parameters are ──────────────────────────────────────────────────
 *
 * `pid=website` and `c=app_store_cta` are baked into the registered link, so
 * nothing here has to repeat them. What IS added per click is `c`, overriding
 * the saved campaign with the SECTION that produced the tap — the AppsFlyer
 * campaign dimension is worth more as six readable buckets than as one — and
 * `af_sub1`, a free-form field and the right home for a value that grows by one
 * every time an article is published.
 */
const ONELINK = "https://bibleshield.onelink.me/ocTL/web";

/**
 * Which section produced a click, from the `from` value the button already
 * sends to PostHog.
 *
 * Deliberately coarse. Thirty-five campaign values — one per article, quiz,
 * collection and passage — would make the AppsFlyer campaign report unreadable
 * for the sake of a breakdown that already exists in PostHog and rides here in
 * `af_sub1` anyway.
 */
function section(from: string): string {
  if (from === "hero" || from === "closing" || from === "header") {
    return "landing";
  }
  if (from.startsWith("share_")) return "share";
  const prefix = from.split(":")[0];
  switch (prefix) {
    case "article":
      return "blog";
    case "quiz":
      return "quiz";
    case "verses":
      return "verses";
    case "verse":
      return "passage";
    default:
      return "web";
  }
}

/**
 * The App Store button's href, carrying attribution.
 *
 * `from` is the same string the button passes to `web_store_click`, so the two
 * halves of the funnel — the tap we measure and the install AppsFlyer measures
 * — can be joined on one value rather than guessed at.
 *
 * Note what this is NOT used for: `installUrl`, `sameAs`, and `llms.txt` all
 * keep `APP_STORE_URL`. A crawler asking what this app is should be told the
 * listing, not handed a redirect.
 */
export function appStoreLink(from: string): string {
  const params = new URLSearchParams({
    c: section(from),
    af_sub1: from,
  });
  return `${ONELINK}?${params.toString()}`;
}
